/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  isEqual,
  mapValues,
  get,
  max,
  cloneDeep,
  isEmpty,
  omit,
  some,
  isUndefined
} from 'lodash';
import { Col, Row, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import numeral from 'numeral';

import { config, columns, defaultSorts, affinityFilters } from './config';

import Iframe from '../../components/Iframe';
import PageHeader from '../../components/PageHeader';
import { TabbedFilters, FilterTypes } from '../../components/Filters';
import AffinityFilters from './AffinityFilters';
import Disclaimer from '../../components/Disclaimer';
import TitleBanner from '../../components/TitleBanner';
import { extractFilterValues } from '../../utils/filters';
import Toggle from '../../components/Filters/Filter/Toggle';
import {
  CountryDemo,
  AgeDemo,
  GenderDemo
} from '../../components/Charts/DemoCharts';
import Table from '../../components/Table';
import InlineQuery from '../../components/InlineQuery';
import {
  getChangedFilters,
  isExpectedFilters,
  getFilterNames
} from '../../utils/filters';
import { exportReportWithHeaders } from '../../utils/export';
import ModuleExport from '../../components/ModuleExport';

const AffinityReport = ({
  blocked,
  filters,
  queries,
  filtersActions: { getFiltersForPageAsync, updateFilters, clearFilters },
  queryActions: { runFormattedInlineQueriesForPage },
  segmentActions: { openAudienceSegmentWindow },
  allowedCountries
}) => {
  const [selectedFilters, setSelectedFilters] = useState(
    extractFilterValues(filters.fields)
  );
  const [selectedBools, setSelectedBools] = useState({});
  const [showTable, setShowTable] = useState(false);
  const [activeDemo, setActiveDemo] = useState('age');

  const getAffinityQuery = () =>
    filters.fields.title.booleanSeparator === 'OR'
      ? 'affinityOR'
      : 'affinityAND';

  const setupIndexQueries = (
    pageConfig,
    isBooleanSeparatorChanged = false,
    changedFilters
  ) => {
    const filterOverrides = {};
    const queriesToRemove = [];

    if (isBooleanSeparatorChanged) {
      filterOverrides.affinityType = {
        id: 'affinityType',
        value: { value: get(filters, 'fields.title.booleanSeparator', 'OR') }
      };
    }

    if (
      isBooleanSeparatorChanged ||
      isExpectedFilters(changedFilters, pageConfig.queries.titleAgeIndex.fields)
    ) {
      filterOverrides.ageDemo = { id: 'ageDemo', value: { value: 'age' } };
    } else {
      queriesToRemove.push('titleAgeIndex');
    }

    if (
      isBooleanSeparatorChanged ||
      isExpectedFilters(
        changedFilters,
        pageConfig.queries.titleGenderIndex.fields
      )
    ) {
      filterOverrides.genderDemo = {
        id: 'genderDemo',
        value: { value: 'gender' }
      };
    } else {
      queriesToRemove.push('titleGenderIndex');
    }

    if (
      isBooleanSeparatorChanged ||
      isExpectedFilters(
        changedFilters,
        pageConfig.queries.titleCountryIndex.fields
      )
    ) {
      filterOverrides.countryDemo = {
        id: 'countryDemo',
        value: { value: 'country' }
      };
    } else {
      queriesToRemove.push('titleCountryIndex');
    }

    return {
      queriesToRemove,
      filterOverrides
    };
  };

  const updateQueries = ({
    isBooleanSeparatorChanged = false,
    changedFilters = []
  }) => {
    const pageConfig = cloneDeep(config.AFFINITY_REPORT_FILTERS);

    if (get(filters, 'fields.title.booleanSeparator', false)) {
      if (filters.fields.title.booleanSeparator === 'OR') {
        delete pageConfig.queries.affinityAND;
      } else {
        delete pageConfig.queries.affinityOR;
      }
    }

    const { filterOverrides, queriesToRemove } = setupIndexQueries(
      pageConfig,
      isBooleanSeparatorChanged,
      changedFilters
    );
    pageConfig.queries = omit(pageConfig.queries, queriesToRemove);

    runFormattedInlineQueriesForPage(pageConfig, {
      ...filters.fields,
      ...filterOverrides
    });
  };

  const onAudienceToggleChange = (id, selection) => {
    setActiveDemo(selection.value);
  };

  useEffect(() => {
    if (!blocked) {
      const values = extractFilterValues(filters.fields);
      // title filter is required
      const hasNewValues =
        !isEmpty(get(filters.fields, 'title.value')) &&
        !isEqual(values, selectedFilters);

      const bools = mapValues(filters.fields, 'booleanSeparator');
      const hasNewBools = !isEqual(
        get(bools, 'title', 'OR'),
        get(selectedBools, 'title', 'OR')
      );
      const contentFields = affinityFilters.content;
      let hasContentValues = false;
      values.forEach(v => {
        const [k] = v.split(':');
        if (contentFields.filter(cf => cf.id === k).length > 0) {
          hasContentValues = true;
        }
      });
      setShowTable(hasContentValues);

      if (hasNewValues) {
        setSelectedFilters(values);
      }
      if (hasNewBools) {
        setSelectedBools(bools);
      }

      if ((hasNewValues || hasNewBools) && values.length) {
        const changedFilters = getChangedFilters(values, selectedFilters);
        const changedFiltersNames = getFilterNames(changedFilters);
        updateQueries({
          isBooleanSeparatorChanged: hasNewBools,
          changedFilters: changedFiltersNames
        });
      }

      if (selectedFilters.length === 0 && !Object.keys(filters.fields).length) {
        // handle "clear all" from TabbedFilter component
        getFiltersForPageAsync(config.AFFINITY_REPORT_PAGE_ID);
      }
    }
  }, [filters.fields]);

  useEffect(() => {
    getFiltersForPageAsync(config.AFFINITY_REPORT_PAGE_ID);
    document.title = 'Affinity | CVM Insights';
  }, []);

  const sampleSize = d =>
    d.reduce((accum, current) => (accum += current.viewers), 0);

  const isAgeDemo = activeDemo => activeDemo === 'age';
  const isGenderDemo = activeDemo => activeDemo === 'gender';
  const isCountryDemo = activeDemo => activeDemo === 'country';

  let demoData;
  let demoExportName;
  let demoExportData;
  if (isAgeDemo(activeDemo)) {
    demoData = queries.titleAgeIndex && queries.titleAgeIndex.data;
    demoExportName = 'affinitySnapshotAge';
  }
  if (isGenderDemo(activeDemo)) {
    demoData = queries.titleGenderIndex && queries.titleGenderIndex.data;
    demoExportName = 'affinitySnapshotGender';
  }
  if (isCountryDemo(activeDemo)) {
    demoData = queries.titleCountryIndex && queries.titleCountryIndex.data;
    demoExportName = 'affinitySnapshotCountry';
  }
  demoExportData = exportReportWithHeaders(demoData);

  const size = numeral(sampleSize(demoData)).format('0.0a');

  const showDemoChart = () => {
    if (isAgeDemo(activeDemo)) {
      return (
        <AgeDemo
          query={queries.titleAgeIndex}
          sortKey="age"
          indexKey="age"
          keys={['index']}
          tooltipFormatter={({ value }) =>
            `${numeral(value * 100).format('0.0')}%`
          }
          showSize={false}
        />
      );
    } else if (isGenderDemo(activeDemo)) {
      const formatDemo = ({ data, loading }) => {
        if (loading) {
          return [];
        }

        return data.map(g => {
          const { gender, index, trackers } = g;
          const viewerIndex = numeral(index * 100).format('0.0');
          return {
            id: gender,
            value: viewerIndex,
            viewers: trackers,
            label: (
              <tspan>
                <tspan className="user-demo-tspan__gender-gender">
                  {gender}
                </tspan>
                <tspan className="user-demo-tspan__gender-viewerIndex" x={80}>
                  {`${viewerIndex}%`}
                </tspan>
              </tspan>
            )
          };
        });
      };
      return (
        <GenderDemo
          label="gender"
          value="index"
          showSize={false}
          query={{
            loading: queries.titleGenderIndex.loading,
            data: formatDemo(queries.titleGenderIndex)
          }}
        />
      );
    } else if (isCountryDemo(activeDemo)) {
      let maxValue;

      if (!queries.titleCountryIndex.loading) {
        maxValue = max(
          queries.titleCountryIndex.data.map(c => Math.abs(c.index))
        );
      }
      return (
        <CountryDemo
          maxValue={maxValue}
          query={queries.titleCountryIndex}
          showSize={false}
          keys={['index']}
          indexKey="country"
          valueKey="index"
        />
      );
    }
  };

  const demoToggleOpts = [
    { value: 'age', label: 'age' },
    { value: 'gender', label: 'gender' },
    { value: 'country', label: 'country' }
  ];

  const demoToggleValue = demoToggleOpts.filter(
    obj => obj.value === activeDemo
  );

  const showContent = () =>
    showTable &&
    get(filters, 'fields.title.value', false) &&
    get(filters, 'fields.title.value', []).length > 0;

  return (
    <>
      <TitleBanner>
        <PageHeader
          title="Affinity"
          tooltip="Affinity evaluates content clustering based on overlapping viewership. The report displays a list of content a predefined audience will likely have interest in."
        />
      </TitleBanner>
      {blocked && (
        <Iframe
          blocked
          blockedMessage="Contact your Client Success Representative to learn more about this report"
          url=""
          height="100%"
        />
      )}
      {!blocked && Object.keys(filters.fields).length > 0 && (
        <AffinityFilters
          filterFields={filters.fields}
          onApply={updateFilters}
          onReset={clearFilters}
          showTable={showTable}
          openSegment={openAudienceSegmentWindow}
          allowedCountries={allowedCountries}
        />
      )}

      {!blocked && (
        <>
          <section className="tvt-main__content">
            <Row
              className={`tvt-affinity__main-content ${
                !showContent() ? 'hide-content' : ''
              }`}
            >
              <Col className="tvt-affinity__main-content-left-col" md={8}>
                <TabbedFilters
                  requiredSegmentValues={segmentFilters => {
                    return some(['show', 'movie'], requiredValue => {
                      const selectedFilter = segmentFilters.find(
                        filterValue => filterValue.id === requiredValue
                      );
                      return !isUndefined(selectedFilter);
                    });
                  }}
                  hasBoolean
                  hideFilters={[
                    'title',
                    'panel',
                    'age',
                    'gender',
                    'date',
                    'segments',
                    'country'
                  ]}
                />
                {showContent() && (
                  <>
                    <InlineQuery
                      useChildForLoading
                      query={queries[getAffinityQuery()]}
                    >
                      {query => (
                        <Table
                          fetchData={false}
                          data={query.data}
                          csv2xls
                          headerFormatter={columns}
                          defaultSorts={defaultSorts}
                          view={getAffinityQuery()}
                          height={438}
                          width={763.3}
                          filters={filters.fields}
                          showRank={false}
                          queryMeta={
                            config.AFFINITY_REPORT_FILTERS.queries[
                              getAffinityQuery()
                            ]
                          }
                        />
                      )}
                    </InlineQuery>
                  </>
                )}
              </Col>
              {showContent() && (
                <>
                  <Col className="tvt-affinity__audience-demo-section" md={4}>
                    <Row className="tvt-affinity-audiencesnapshot">
                      <Col className="col-xs-9 tvt-affinity-audiencesnapshot-titlearea">
                        <div className="tvt-affinity-audiencesnapshot-title">
                          Audience Snapshot
                          <OverlayTrigger
                            placement="bottom"
                            overlay={
                              <Tooltip id="tvt-anticipation-tracker__item-tooltip">
                                <div>
                                  This metric represents a normalized view of
                                  this defined audience. It is calculated
                                  relative to the overall app population to
                                  adjust for app population biases. The
                                  percentages represented do not reflect raw
                                  viewer demographics.
                                </div>
                              </Tooltip>
                            }
                          >
                            <FontAwesomeIcon
                              className="benchmark-info-icon"
                              size="1x"
                              icon="info-circle"
                            />
                          </OverlayTrigger>
                        </div>

                        <div className="tvt-affinity-audiencesnapshot-samplesize">
                          Sample Size: {size}
                        </div>
                      </Col>
                      <Col className="col-xs-3 text-right tvt-affinity-audiencesnapshot-export">
                        <ModuleExport
                          fetchData={false}
                          showTitle={demoExportName}
                          data={demoExportData}
                          csv2xls
                          csv
                          xls
                        />
                      </Col>
                    </Row>

                    <Toggle
                      id="affinity-audience"
                      value={demoToggleValue}
                      options={demoToggleOpts}
                      onChange={onAudienceToggleChange}
                      alwaysShowDivider
                    />
                    <div className="tvt-affinity__audience-demo-wrapper">
                      {showDemoChart()}
                    </div>
                  </Col>
                </>
              )}
            </Row>
            {!showTable && (
              <div style={{ margin: '40px 0 20px' }}>
                <Disclaimer>
                  <div className="affinity-report-disclaimer-wrapper">
                    To generate the report, select from the above criteria in
                    the Audience Builder to define your seed audience, or use an
                    existing segment.
                  </div>
                </Disclaimer>
              </div>
            )}
          </section>
        </>
      )}
    </>
  );
};

AffinityReport.propTypes = {
  blocked: PropTypes.bool.isRequired,

  filters: PropTypes.shape({
    fields: FilterTypes.FilterSetType, // eslint-disable-line react/no-typos
    groupings: FilterTypes.GroupingSetType // eslint-disable-line react/no-typos
  }).isRequired,

  filtersActions: PropTypes.shape({
    getFiltersForPageAsync: PropTypes.func.isRequired,
    updateFilters: PropTypes.func.isRequired,
    clearFilters: PropTypes.func.isRequired
  }).isRequired,
  segmentActions: PropTypes.shape({
    openAudienceSegmentWindow: PropTypes.func.isRequired
  }).isRequired,
  queryActions: PropTypes.shape({
    runFormattedInlineQueriesForPage: PropTypes.func.isRequired
  }).isRequired,

  looks: PropTypes.shape({
    affinityReport: PropTypes.shape({})
  }).isRequired,

  queries: PropTypes.shape({
    titleAgeIndex: PropTypes.shape({
      loading: PropTypes.bool.isRequired,
      data: PropTypes.arrayOf(PropTypes.shape({}))
    }),
    titleGenderIndex: PropTypes.shape({
      loading: PropTypes.bool.isRequired,
      data: PropTypes.arrayOf(PropTypes.shape({}))
    }),
    titleCountryIndex: PropTypes.shape({
      loading: PropTypes.bool.isRequired,
      data: PropTypes.arrayOf(PropTypes.shape({}))
    }),
    affinityOR: PropTypes.shape({
      loading: PropTypes.bool.isRequired,
      data: PropTypes.arrayOf(PropTypes.shape({}))
    }),
    affinityAND: PropTypes.shape({
      loading: PropTypes.bool.isRequired,
      data: PropTypes.arrayOf(PropTypes.shape({}))
    })
  }).isRequired
};

export default AffinityReport;
